export namespace DOM {

    export class Parser {

        public static getBodyElement(): HTMLBodyElement {

            const bodyElement = document.getElementsByTagName('body');
            if (bodyElement.length !== 1) { throw new Error(`DOM can't find body element`); }
            return bodyElement[0];
        }

        public static getLocale(): "en"|"cs" {

            const htmlElement = document.getElementsByTagName('html');
            if (htmlElement.length !== 1) { throw new Error(`DOM can't find html element`); }
            const lang = htmlElement[0].getAttribute('lang');
            if (lang !== 'en' && lang !== 'cs') { throw new Error(`Wrong localization on HTML element: ${lang}`); }
            return lang;
        }

        public static getParentElement(element: HTMLElement): HTMLElement {

            if (!element.parentElement) { throw new Error(`DOM can't find parent element`); }
            return element.parentElement;
        }

        public static getHtmlElement(id: string, inside?: HTMLElement): HTMLElement {

            const element: HTMLElement|null = inside ? inside.querySelector('#' + id) :  document.getElementById(id);
            if (!element) { throw new Error(`DOM can't find element with id ${id}`); }
            return element;
        }

        public static getHtmlElementOrNull(id: string, inside?: HTMLElement): HTMLElement|null {
            return inside ? inside.querySelector('#' + id) : document.getElementById(id);
        }

        public static getHtmlElementByClass(className: string, inside?: Element): HTMLElement {

            let elements: HTMLCollectionOf<Element>|null;
            if (inside) { elements = inside.getElementsByClassName(className); } else { elements = document.getElementsByClassName(className); }
            const element: Element|null = elements ? elements.item(0) : null;
            if (!element) { throw new Error(`DOM can't find element with class ${className}`); } else { return element as HTMLElement; }
        }

        public static getHtmlInputElementValueByName(name: string, inside ?: HTMLElement): string|null {

            const element: HTMLInputElement|null =  inside
                ? inside.querySelector(`[name='${name}']`)
                : document.querySelector(`[name='${name}']`);
            if (element === null) { throw new Error(`DOM can't find element with name ${name}`); }
            return element.value.length > 0 ? element.value : null;
        }

        public static getHtmlInputElementByName(name: string): HTMLElement {

            const elements: NodeListOf<HTMLElement>|null = document.getElementsByName(name);
            if (elements.length === 0) { throw new Error(`DOM can't find elements with name ${name}`); }
            return elements[0];
        }

        public static getHtmlElements(className: string, inside ?: Element, ignoreEmpty: boolean = true): HTMLCollectionOf<HTMLElement> {

            const elements: HTMLCollectionOf<Element> = inside ? inside.getElementsByClassName(className) : document.getElementsByClassName(className);
            if (!ignoreEmpty && elements.length === 0) { throw new Error(`DOM can't find elements with class ${className}`); }
            return elements as HTMLCollectionOf<HTMLElement>;
        }

        public static checkFormInvalid(form: HTMLFormElement): boolean {

            const elementsNotVerified: HTMLCollectionOf<HTMLElement> = DOM.Parser.getHtmlElements('NOT_VERIFIED', form, true);
            const elementsNotFilled: HTMLCollectionOf<HTMLElement> = DOM.Parser.getHtmlElements('NOT_FILLED', form, true);
            const elementsInvalid: NodeListOf<HTMLElement> = form.querySelectorAll(`[data-valid='false']`);
            return (elementsNotVerified.length + elementsInvalid.length + elementsNotFilled.length) !== 0;
        }

        public static getHtmlElementsByQuery(query: string,  inside ?: Element, mute = false): NodeListOf<HTMLElement> {

            const elements: NodeListOf<HTMLElement> = inside ? inside.querySelectorAll(query) : document.querySelectorAll(query);
            if (!mute && elements.length === 0) { throw new Error(`DOM can't find elements with query ${query}`); }
            return elements;
        }

        public static getHtmlElementByQuery(query: string,  inside ?: Element): HTMLElement {

            const elements = this.getHtmlElementsByQuery(query, inside, false);
            return elements[0];
        }

        // noinspection JSUnusedGlobalSymbols
        public static getHtmlElementAttribute(element: HTMLElement, attributeName: string): string {

            const attributeValue: string|null = element.getAttribute(attributeName);
            if (!attributeValue) { throw new Error(`DOM can't find attribute ${attributeName} on element`); }
            return attributeValue;
        }

        public static getElementByTag(tagName: string, inside ?: Element, mute = false): HTMLElement {

            let element : HTMLCollectionOf<HTMLElement>|null;
            if (inside) element = inside.getElementsByTagName(tagName) as HTMLCollectionOf<HTMLElement>;
            else element = document.getElementsByTagName(tagName) as HTMLCollectionOf<HTMLElement>;
            if (!mute && element.length === 0) { throw new Error(`DOM can't find element with tag ${tagName}`); }
            return element[0];
        }

        public static getDataByNameFromElement(dataName: string, element: HTMLElement): string {

            const dataValue = element.dataset[dataName];
            if (!dataValue) { throw new Error(`Missing data with key ${dataName} on element`); }
            return dataValue;
        }

        public static getDataByNameFromElementOrNull(dataName: string, element: HTMLElement): string|null {

            const dataValue = element.dataset[dataName];
            return dataValue && dataValue.toString().trim().length > 0 ? dataValue : null;
        }

        public static getElementsByDataName(dataName: string, element?: HTMLElement, mute = false): NodeListOf<HTMLElement> {
            return this.getHtmlElementsByQuery(`[data-${dataName}]`, element, mute);
        }

        public static parentContainsTheClass(className: string, element: HTMLElement): boolean {

            if (!element.parentElement) { return false; }
            if (element.classList.contains(className)) { return true; }
            return this.parentContainsTheClass(className, element.parentElement);
        }

        /**
         * Allow to refresh all events on element
         * Must be in container
         * @param elementId - element dom ID to re-get element
         */
        public static reInitializeElement(elementId: string) : HTMLElement {

            let element = DOM.Parser.getHtmlElement(elementId);
            const elementParent = element.parentElement;
            if (elementParent === null) { throw new Error(`Missing parent on element`); }
            const elementCloned = element.cloneNode(true)
            element.remove();
            elementParent.append(elementCloned);
            return DOM.Parser.getHtmlElement(elementId);
        }
    }
}
